import { EmbeddedCompletionEnum, type EmbeddedElementValue } from '@vedalib/editor/lib/elements'
import { EmbeddedSourceEnum } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import { chainName, iframeLinkValidation } from 'utils'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldFile, FieldInput, FieldSelect, FormField } from 'components/formKit'
import { useFormData, useFormOnChangeValue } from 'components/formKit/hooks'
import type { IFieldChange, IFieldMeta } from 'components/formKit/types'
import { IconButton } from 'components/uiKit/Button'
import Divider from 'components/uiKit/Divider'
import { KitSize } from 'components/uiKit/KitTypes'
import Link from 'components/uiKit/Link'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import { Typography } from 'components/uiKit/Typography'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import * as s from './EmbeddedFormValue.module.scss'
import FieldCodeEditor from './components/FieldCodeEditor'
import type { EmbeddedValueFormType } from './embeddedTypes'

const NOTICE_TEXT = "window.parent.postMessage({ type: 'complete' }, '*');"

const HALP_INFO_CONFIG = {
  [EmbeddedSourceEnum.html]: {
    textLink: t('elements.embedded.form.help.html.textLink'),
    link: window.PLATFORM.embeddedHelp.html || '',
    hint: t('elements.embedded.form.help.html.hint'),
  },
  [EmbeddedSourceEnum.link]: {
    textLink: t('elements.embedded.form.help.link.textText'),
    link: window.PLATFORM.unsupportedLinksHelp || '',
    hint: t('elements.embedded.form.help.link.hint'),
  },
  [EmbeddedSourceEnum.zip]: {
    textLink: t('elements.embedded.form.help.archive.textLink'),
    link: window.PLATFORM.embeddedHelp.archive || '',
    hint: t('elements.embedded.form.help.archive.hint'),
  },
}

const NOTICE_INDENTS = { marginTop: 8 }

const useChangeCompletionRule = (_params: IFieldConfig<Block>) => {
  const onChangeValue = useFormOnChangeValue()
  return {
    effect: ({ value, name }: IFieldChange<EmbeddedSourceEnum, IFieldMeta>) => {
      if (value !== EmbeddedSourceEnum.zip && name) {
        onChangeValue?.({
          name: name.replace('type', 'completion'),
          value: EmbeddedCompletionEnum.none,
        })
      }
      return { name, value }
    },
  }
}

const useShowHTML = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<EmbeddedElementValue>(name, 1)
  const hidden = value?.type !== EmbeddedSourceEnum.html
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowZIP = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<EmbeddedElementValue>(name, 1)
  const hidden = value?.type !== EmbeddedSourceEnum.zip
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowLink = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<EmbeddedElementValue>(name, 1)
  const hidden = value?.type !== EmbeddedSourceEnum.link
  return useMemo(() => ({ hidden }), [hidden])
}

const TYPE = genElementValueField({
  as: FieldSelect<EmbeddedSourceEnum>,
  label: t('elements.embedded.form.type'),
  name: 'type',
  props: {
    fluid: true,
    options: [
      { label: t('input.option.html'), value: EmbeddedSourceEnum.html },
      { label: t('input.option.link'), value: EmbeddedSourceEnum.link },
      { label: t('input.option.archive'), value: EmbeddedSourceEnum.zip },
    ],
  },
  useConfigs: [useChangeCompletionRule],
})

const HTML = genElementValueField({
  as: FieldCodeEditor,
  name: 'html',
  layout: 'vertical',
  useConfigs: [useShowHTML],
})

const ZIP_FILE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'zipFile',
  props: { nullable: true, fileType: 'document' },
  useConfigs: [useShowZIP],
})

const ZIP_PATH = genElementValueField({
  as: FieldInput,
  name: 'zipPath',
  label: t('elements.embedded.form.path'),
  props: { placeholder: 'index.html' },
  useConfigs: [useShowZIP],
})

const LINK = genElementValueField({
  as: FieldInput,
  name: 'link',
  label: t('input.label.linkAddress'),
  props: { placeholder: t('input.placeholder.enterUrl') },
  useConfigs: [useShowLink],
  rules: [
    {
      max: 2048,
      validator: iframeLinkValidation,
      message: t('input.error.invalidUrl'),
    },
  ],
})

const COMPLETION_RULE = genElementValueField({
  as: FieldSelect<EmbeddedCompletionEnum>,
  label: t('elements.embedded.form.completion'),
  name: 'completion',
  props: {
    fluid: true,
    options: [
      getEnumOption('EmbeddedCompletionEnum', EmbeddedCompletionEnum.none),
      getEnumOption('EmbeddedCompletionEnum', EmbeddedCompletionEnum.scormPassed),
      getEnumOption('EmbeddedCompletionEnum', EmbeddedCompletionEnum.click),
    ],
  },
  defaultValue: EmbeddedCompletionEnum.none,
})

const EmbeddedForm: EmbeddedValueFormType = ({ name, element }) => {
  const type = element.value.type
  const showCompletionNotice = element.value.completion === EmbeddedCompletionEnum.scormPassed
  const showNoticeOnClick = element.value.completion === EmbeddedCompletionEnum.click
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(NOTICE_TEXT)
      notify({ type: NotificationType.success, message: t('notify.textCopied') })
    } catch (err) {
      notify({
        type: NotificationType.error,
        message: t('notify.copiedError'),
        duration: 2000,
      })
    }
  }, [])

  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, TYPE)} />
      <FormField config={chainName(name, HTML)} />
      <FormField config={chainName(name, LINK)} />
      <FormField config={chainName(name, ZIP_FILE)} />
      <FormField config={chainName(name, ZIP_PATH)} />
      {showNoticeOnClick && (
        <Typography indents={NOTICE_INDENTS} styleType='hint'>
          {t('elements.embedded.form.completion.click.hint')}
        </Typography>
      )}
      {showCompletionNotice && (
        <>
          <Typography indents={NOTICE_INDENTS} styleType='hint'>
            {t('elements.embedded.form.completion.hint')}
          </Typography>
          <div className={s.notice}>
            <Typography wordBreak='break-all'>{NOTICE_TEXT}</Typography>
            <IconButton
              icon='otherCopy'
              name='otherCopy'
              onClick={handleCopy}
              size={KitSize.XS}
              styleType='ghost'
            />
          </div>
        </>
      )}
      <FormField config={chainName(name, COMPLETION_RULE)} />
      <Divider free />
      <Typography styleType='hint' textAlign='center'>
        {HALP_INFO_CONFIG[type].hint}
        <Link name='helpLink' target='_blank' to={HALP_INFO_CONFIG[type].link}>
          {HALP_INFO_CONFIG[type].textLink}
        </Link>
      </Typography>
    </Padding>
  )
}

export default EmbeddedForm
